/* eslint-disable no-nested-ternary */
import { Pressable } from 'react-aria-components';
import { useState } from 'react';

import { useIsMobile } from 'hooks';
import { completeLookUnbxd, saveImageForSearch } from 'actions/search';

import { Button } from 'components/ui/button';
import { DialogRoot, Dialog, DialogHeader } from 'components/ui/dialog';
import { ProgressCircle } from 'components/ui/progress-circle';

import { PersonalShopperSVG, SimilarPairSVG, CompleteLookSVG, PersonalShopperUploaderIconSVG } from 'components/iconsSVG/personalShopper';

import { useI18n } from 'context/i18n';

import FileUploader from 'components/fileUploader';
import { Tooltip } from 'components/ui/tooltip';
import { fileToBase64 } from 'utils/helpers';

import { dataLayerHandleEvent } from 'utils/dataLayers';

import styles from './style.module.css';

const ShopperOption = ({ icon, text, onClick }: { icon: React.ReactNode; text: string; onClick: () => void }) => {
  return (
    <Pressable onPress={onClick}>
      <div role="button" className="mt-5 flex cursor-pointer items-center rounded-lg border border-solid border-[#919191] px-4 py-2.5 md:m-4 md:mt-8">
        <div className="mr-2 min-w-[68px]">{icon}</div>
        <p className="m-0">{text}</p>
      </div>
    </Pressable>
  );
};

const PersonalShopper = ({ autosuggestVisible }: { autosuggestVisible: boolean }) => {
  const { t, locale } = useI18n();

  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [mode, setMode] = useState<string | null>(null);
  const [files, setFiles] = useState([]);
  const [fileErrors, setFilesErrors] = useState([]);
  const [error, setError] = useState<string | null>(null);

  const handleClickPersonalShopperButton = () => {
    dataLayerHandleEvent({
      event: 'main_menu',
      event_category: 'menu',
      event_detail_1: 'find similar and complete the look',
      event_detail_2: '',
    });
    setModalVisible((current) => !current);
  };

  const handleFindSimilarPair = async (file: TODO[]) => {
    dataLayerHandleEvent({
      event: 'upload_photo',
      event_category: 'find similar and complete the look',
      event_detail_1: 'find similar',
      event_detail_2: '',
    });
    setLoading(true);

    const sourceFile = file?.[0]?.src?.file;
    const base64Image = await fileToBase64(sourceFile);

    const searchID = await saveImageForSearch({ profile: locale, encodedImage: base64Image });

    if (searchID) {
      window.location.assign(`${window.location.origin}/${locale}/search?mode=findSimilar&serp=new&target=A&imageID=${searchID}`);
    } else {
      setLoading(false);
      setError(t('generico', 'complete.look.error'));
    }
  };

  const handleCompleteLook = async (file: TODO[]) => {
    dataLayerHandleEvent({
      event: 'upload_photo',
      event_category: 'find similar and complete the look',
      event_detail_1: 'complete the look',
      event_detail_2: '',
    });
    setLoading(true);
    setError(null);
    const { valid, query, errors } = await completeLookUnbxd({
      profile: locale,
      image: file?.[0]?.src?.file,
    });

    if (valid === false) {
      setLoading(false);
      setError(t('generico', 'complete.look.error'));
      setFiles([]);
    } else {
      window.location.assign(`${window.location.origin}/${locale}/search?searchTerm=${query}&mode=completeLook&serp=new&target=A`);
    }
  };

  const handleClickDefaultScreen = (variant: string) => {
    const eventName = variant === 'similar' ? 'find_similar' : 'complete_the_look';

    dataLayerHandleEvent({
      event: eventName,
      event_category: 'find similar and complete the look',
      event_detail_1: '',
      event_detail_2: '',
    });
    setMode(variant);
  };

  const DefaultScreen = () => (
    <>
      <p className="mb-0 md:mx-auto md:w-[80%] md:justify-self-center md:text-center">{t('generico', 'personal.shopper.description')}</p>

      <div className="flex flex-col text-[#919191] md:mt-9 md:flex-row md:justify-center">
        <ShopperOption icon={<SimilarPairSVG />} text={t('generico', 'find.similar.pair')} onClick={() => handleClickDefaultScreen('similar')} />
        <ShopperOption icon={<CompleteLookSVG />} text={t('generico', 'complete.your.look')} onClick={() => handleClickDefaultScreen('complete')} />
      </div>
    </>
  );

  const UploadPhotoScreen = ({ name, description, callback }: { name: string; description: string; callback: TODO }) => (
    <form name={name} encType="multipart/form-data">
      <p className="md:text-center">{description}</p>
      <FileUploader
        minCount={1}
        maxCount={1}
        files={files}
        setFiles={setFiles}
        errors={fileErrors}
        setErrors={setFilesErrors}
        sizeLimitMB={50}
        inputText={t('generico', 'upload.photo')}
        className="mt-20 flex cursor-pointer items-center justify-around bg-[#F6F6F6] p-4"
        icon={(<PersonalShopperUploaderIconSVG />) as TODO}
        handleSuccess={callback}
        key={name}
      />
      {error && <p className="error">{error}</p>}
    </form>
  );

  const screenMap = {
    similar: <UploadPhotoScreen name="findSimilar" description={t('generico', 'find.similar.pair.description')} callback={handleFindSimilarPair} />,
    complete: <UploadPhotoScreen name="completeLook" description={t('generico', 'complete.look.description')} callback={handleCompleteLook} />,
  };

  return (
    <div
      className={`absolute right-[50px] top-0 z-[1000] h-5 w-5 p-1.5 ${isMobile ? 'transition-none' : 'transition-all'} mobileXL:right-[80px] md:visible pattersonMD:top-[1px] ${autosuggestVisible ? 'visible pattersonMD:right-[35px] lg:right-[50px]' : 'lg:right[25px] invisible pattersonMD:right-[25px]'} ${styles.personalShopperWrapper}`}
    >
      <Tooltip placement="bottom" message={<span>{t('generico', 'shoogle.tooltip')}</span>}>
        <Button variant="none" onPress={handleClickPersonalShopperButton}>
          <PersonalShopperSVG />
        </Button>
      </Tooltip>
      <DialogRoot
        isOpen={modalVisible}
        onOpenChange={(open) => {
          setModalVisible(open);
          if (!open) setMode(null);
        }}
        showOverlay
        // className={styles.personalShopperModal}
        position="center"
      >
        <Dialog>
          <DialogHeader label={t('generico', 'shoogle.it')} hidden />
          <div className="md:flex md:flex-col md:items-center md:justify-center">
            <p className="text-2xl">{t('generico', 'shoogle.it')}</p>
            <div className="relative">
              {mode && mode in screenMap ? screenMap[mode as keyof typeof screenMap] : <DefaultScreen />}
              {loading ?
                <ProgressCircle className="absolute inset-0 flex items-center justify-center bg-bold/10 backdrop-blur-sm" svgClassName="size-5" />
              : null}
            </div>
          </div>
          <div className="mt-auto flex flex-col pt-6">
            {mode !== null ?
              <span>{t('generico', 'image.upload.message')}</span>
            : null}
            <span>{t('generico', 'powered.by.ai')}</span>
          </div>
        </Dialog>
      </DialogRoot>
    </div>
  );
};

export default PersonalShopper;
